import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BiRedo, BiSearch, BiUndo, BiArrowBack, BiShareAlt, BiTime, BiBell, BiMenu,
} from 'react-icons/bi';
import { BsEye } from 'react-icons/bs';
import { MdEditNote } from 'react-icons/md';
import { Button } from '../ui';
import { Drawer } from '../ui/Drawer';
import ThemesToggle from '../../ThemesToggle';
import { LS_REDIRECT_URL_KEY } from '../../core/constants/common';
import Utils from '../../core/utilities/Utils';
import { SAVE_STATUS } from '../../features/builder/builderEditorConstants';
import { useBuilderEditor } from '../../features/builder/BuilderEditorContext';
import './builder-shell.scss';

const SaveStatusLabel = ({ status }) => {
  const map = {
    [SAVE_STATUS.SAVED]: { text: 'Saved', className: 'builder-header__save-status--saved' },
    [SAVE_STATUS.SAVING]: { text: 'Saving…', className: 'builder-header__save-status--saving' },
    [SAVE_STATUS.UNSAVED]: { text: 'Unsaved changes', className: 'builder-header__save-status--unsaved' },
    [SAVE_STATUS.ERROR]: { text: 'Save failed', className: 'builder-header__save-status--unsaved' },
  };
  const item = map[status] || { text: '', className: '' };
  return (
    <span className={`builder-header__save-status ${item.className || ''}`} aria-live="polite">
      {item.text}
    </span>
  );
};

const BuilderHeader = ({
  userFullName,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onShare,
  onPublish,
  canPublish,
  isPublished,
  searchRef,
  onToggleSidebar,
  sidebarOpen,
}) => {
  const navigate = useNavigate();
  const {
    formId, langId, formdata, saveStatus, searchQuery, setSearchQuery, setUpdateForm, guardAction,
  } = useBuilderEditor();
  const [showHistory, setShowHistory] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const localSearchRef = useRef(null);
  const inputRef = searchRef || localSearchRef;

  const goPreview = () => {
    guardAction(() => {
      let url = `/form_builder/form_preview/${formId}`;
      if (langId) url += `/${langId}`;
      navigate(url);
    });
  };

  // Mirrors App.js's legacy header Back button exactly: the legacy header
  // (and its working Back button) is hidden while the new Builder is active,
  // so this restores the same behavior here. redirectUrl is read from the
  // same localStorage key App.js populates from the user-details response;
  // handleExit uses the same exit-URL resolution the legacy header uses.
  const redirectUrl = localStorage.getItem(LS_REDIRECT_URL_KEY);

  const handleExit = () => {
    guardAction(() => {
      const url = Utils.getFormattedFormBuilderExitURL();
      if (!url) return;
      window.location.href = url;
    });
  };

  return (
    <>
      <header className="builder-header">
        <div className="builder-header__left">
          {onToggleSidebar && (
            <Button
              variant="ghost"
              icon={<BiMenu />}
              aria-label="Toggle sidebar"
              aria-expanded={sidebarOpen}
              aria-controls="form-sidebar"
              onClick={onToggleSidebar}
            />
          )}
          <span className="builder-header__project-name" title={formdata?.name}>
            {formdata?.name || 'Untitled Form'}
          </span>

          <Button variant="ghost" size="sm" onClick={() => setUpdateForm(true)} aria-label="Rename form">
            Edit
          </Button>
        </div>

        {/* <div className="builder-header__center">
          <div className="builder-header__search">
            <BiSearch aria-hidden="true" />
            <input
              ref={inputRef}
              type="search"
              placeholder="Search sections & questions (Ctrl+K)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search form structure"
            />
          </div>
        </div> */}

        <div className="builder-header__right">
          {/* <Button variant="ghost" icon={<BiUndo />} aria-label="Undo" onClick={onUndo} disabled={!canUndo} /> */}
          {/* <Button variant="ghost" icon={<BiRedo />} aria-label="Redo" onClick={onRedo} disabled={!canRedo} /> */}
          <SaveStatusLabel status={saveStatus} />
          <span className="builder-header__divider" aria-hidden="true" />
          <div className="preview-mode-toggle" role="group" aria-label="Editor mode">
            <button
              type="button"
              className="preview-mode-toggle__btn preview-mode-toggle__btn--active"
              aria-current="page"
            >
              <MdEditNote aria-hidden="true" /> Build
            </button>
            <button
              type="button"
              className="preview-mode-toggle__btn"
              onClick={goPreview}
            >
              <BsEye aria-hidden="true" /> Preview
            </button>
          </div>
          {redirectUrl && (
            <Button
              type="button"
              variant="ghost"
              className="border"
              onClick={handleExit}
            >
              <BiArrowBack />
              Back
            </Button>
          )}
          {canPublish && (
            <Button variant="primary" size="sm" onClick={onPublish}>
              Publish
            </Button>
          )}
          {isPublished && (
            <span className="preview-published-badge">Published</span>
          )}
          {/* <Button variant="ghost" icon={<BiShareAlt />} aria-label="Share" onClick={onShare} /> */}
          {/* <Button variant="ghost" icon={<BiTime />} aria-label="Version history" onClick={() => setShowHistory(true)} /> */}
          {/* <Button variant="ghost" icon={<BiBell />} aria-label="Notifications" onClick={() => setShowNotifications(true)} /> */}
          {userFullName && (
            <span className="builder-text-secondary" style={{ fontSize: 14 }}>{userFullName}</span>
          )}
          <ThemesToggle />
        </div>
      </header>

      <Drawer show={showHistory} onClose={() => setShowHistory(false)} title="Version History">
        <p className="builder-text-secondary">Version history will be available when connected to the backend.</p>
        <ul style={{ marginTop: 16, paddingLeft: 20, color: '#6b7280', fontSize: 13 }}>
          <li>Current version — now</li>
        </ul>
      </Drawer>

      <Drawer show={showNotifications} onClose={() => setShowNotifications(false)} title="Notifications">
        <p className="builder-text-secondary">No new notifications.</p>
      </Drawer>
    </>
  );
};

export default BuilderHeader;
